import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  CircularProgress,
} from '@mui/material';
import { getWelcomeList, interpolateList } from '../../../utils/welcomeText';
import { wlog } from '../../../utils/welcomeDebug';
import { LottieAnimation } from '../../../components/Welcome/LottieAnimation';
import processingLoopAnimation from '../../../assets/welcome/welcome-processing-loop.json';
import processingReducedAnimation from '../../../assets/welcome/welcome-processing-reduced.json';

/**
 * Tela 9 — Processando 🚀
 * Dispara a chamada à API (POST /api/welcome)
 * Mostra mensagens rotativas enquanto processa
 * Trata timeout (30s) e erro
 * Auto-navega para Tela 10 quando result chega (decisão D6)
 */
export function Step9Processing({ wizard, goNext }) {
  wlog('Step9Processing', 'RENDER', {
    isProcessing: wizard.state?.isProcessing,
    hasResult: !!wizard.state?.result,
    error: wizard.state?.error,
  });
  const template = wizard.template;
  const firstName = wizard.state.userName.split(' ')[0].trim();
  
  const fired = useRef(false);
  const timeoutRef = useRef(null);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [timedOut, setTimedOut] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [interpolatedMessages, setInterpolatedMessages] = useState([
    'Preparando tudo para você...',
    'Criando serviços...',
    'Configurando agenda...',
    'Gerando páginas públicas...',
    'Quase lá!',
  ]);

  // Carrega as mensagens rotativas do tema assincronamente
  useEffect(() => {
    (async () => {
      const messages = await getWelcomeList(template, 'processingMessages');
      const defaultMessages = [
        'Preparando tudo para você...',
        'Criando serviços...',
        'Configurando agenda...',
        'Gerando páginas públicas...',
        'Quase lá!',
      ];

      const toInterpolate = messages || defaultMessages;
      const interpolated = await interpolateList(toInterpolate, {
        firstName,
        locationName: wizard.state.locationName,
        businessNoun: template?.welcome?.businessNoun || 'negócio',
      });

      setInterpolatedMessages(interpolated);
    })();
  }, [template, firstName, wizard.state.locationName]);

  // Detecta prefers-reduced-motion
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Dispara a submissão uma única vez
  useEffect(() => {
    if (fired.current) return;
    fired.current = true;

    const submitAndWait = async () => {
      try {
        // Dispara o submit (que internamente faz POST /api/welcome)
        await wizard.submit();

        // Se chegou aqui com sucesso, result foi gravado no state
        // Limpa o timeout e navega para a próxima tela
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        
        // Se há result, navega para Tela 10
        if (wizard.state.result) {
          goNext();
        }
      } catch (error) {
        // Erro foi capturado pelo hook; grava em wizard.state.error
        console.error('Erro no submit:', error);
      }
    };

    submitAndWait();

    // Timeout de 30s: se não houver resultado até lá, tenta GET /api/welcome
    timeoutRef.current = setTimeout(async () => {
      if (!wizard.state.result && !wizard.state.error) {
        setTimedOut(true);
      }
    }, 30000);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // Rotação de mensagens a cada 2s
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % interpolatedMessages.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [interpolatedMessages.length]);

  // Quando o result chega, navega automaticamente (D6)
  useEffect(() => {
    if (wizard.state.result) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      // Pequeno delay para a UI atualizar
      setTimeout(() => {
        goNext();
      }, 500);
    }
  }, [wizard.state.result, goNext]);

  const handleRetry = async () => {
    setIsRetrying(true);
    setTimedOut(false);
    fired.current = false; // Reset para permitir novo submit

    try {
      await wizard.submit();
      if (wizard.state.result) {
        goNext();
      }
    } catch (error) {
      console.error('Erro ao tentar novamente:', error);
    } finally {
      setIsRetrying(false);
    }
  };

  // Renderizar estado de erro
  if (wizard.state.error && !timedOut) {
    return (
      <Container
        maxWidth="sm"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          py: 4,
          textAlign: 'center',
        }}
      >
        <Typography variant="h1" sx={{ fontSize: '24px', fontWeight: 'bold', mb: 2 }}>
          ⚠️ Oops! Algo deu errado
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
          {wizard.state.error.message ||
            'Não conseguimos configurar sua agenda. Mas não se preocupe — dá pra tentar de novo!'}
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={handleRetry}
          disabled={isRetrying}
          sx={{ width: '100%' }}
        >
          {isRetrying ? 'Tentando...' : 'Tentar de novo'}
        </Button>
      </Container>
    );
  }

  // Renderizar estado de timeout
  if (timedOut) {
    return (
      <Container
        maxWidth="sm"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          py: 4,
          textAlign: 'center',
        }}
      >
        <Typography variant="h1" sx={{ fontSize: '24px', fontWeight: 'bold', mb: 2 }}>
          ⏱️ Demorando um pouco...
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
          A configuração está levando mais tempo que o esperado. Quer tentar novamente?
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={handleRetry}
          disabled={isRetrying}
          sx={{ width: '100%' }}
        >
          {isRetrying ? 'Tentando...' : 'Tentar de novo'}
        </Button>
      </Container>
    );
  }

  // Renderizar estado normal (processando)
  const animationData = prefersReducedMotion ? processingReducedAnimation : processingLoopAnimation;

  return (
    <Container
      maxWidth="sm"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        py: 4,
      }}
    >
      <Stack
        alignItems="center"
        spacing={3}
        sx={{
          textAlign: 'center',
        }}
      >
        {/* Lottie Animation or Fallback */}
        <Box
          sx={{
            width: { xs: 180, sm: 240 },
            height: { xs: 180, sm: 240 },
            maxWidth: '80vw',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LottieAnimation
            animationData={animationData}
            loop={true}
            autoplay={true}
            style={{ width: '100%', height: '100%' }}
            fallback={<CircularProgress size={64} />}
          />
        </Box>

        <Typography
          variant="h1"
          sx={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: 'text.primary',
          }}
        >
          🚀 Preparando tudo para você...
        </Typography>

        <Typography
          variant="body1"
          sx={{
            minHeight: '24px',
            color: 'text.secondary',
            transition: 'opacity 0.3s ease',
          }}
        >
          {interpolatedMessages[currentMessageIndex]}
        </Typography>
      </Stack>
    </Container>
  );
}

export default Step9Processing;
